import { Link } from 'react-router-dom';

export interface ErrorFallbackProps {
  error: Error;
  onRetry: () => void;
  /** Offer a way back to the document list — not useful when already there. */
  showHome?: boolean;
}

/**
 * What a crash looks like.
 *
 * The point is that it is not a blank page. A document whose content makes the
 * editor throw would otherwise be unopenable for ever, with nothing on screen
 * to say why and no way back to the other documents.
 *
 * The message is shown because it is often the only clue anyone has, and this
 * app has no server-side log to consult. The stack stays in the console.
 */
export function ErrorFallback({ error, onRetry, showHome = true }: ErrorFallbackProps) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-lg font-semibold text-neutral-900">Something went wrong</h1>
        <p className="text-sm text-neutral-600">
          Your documents are stored in this browser and have not been lost. Changes made
          in the last few seconds before the error may not have been saved.
        </p>
        <p
          className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-left font-mono text-xs break-words text-amber-900"
          role="alert"
        >
          {error.message || 'An unknown error occurred.'}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          className="rounded bg-blue-700 px-3 py-1.5 text-sm text-white hover:bg-blue-800"
          onClick={onRetry}
        >
          Try again
        </button>
        {showHome && (
          <Link
            to="/"
            className="rounded border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Back to documents
          </Link>
        )}
        <button
          type="button"
          className="rounded border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50"
          onClick={() => window.location.reload()}
        >
          Reload the app
        </button>
      </div>
    </div>
  );
}
